import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TrendingUp, Play, Square, RefreshCw, Settings, Clock, CheckCircle, XCircle, ChevronDown, ChevronRight, Activity } from "lucide-react";
import { TradingViewCrawlerService, type CrawlJob } from '@/services/TradingViewCrawlerService';
import { AppSettingsService } from '@/services/app-settings-service';
import { useCrawlJobRealtime } from '@/hooks/use-crawl-job-realtime';
import { useCrawlJobsHealth } from '@/hooks/use-crawl-jobs-health';
import { CrawlJobHealthIndicator } from '@/components/admin/crawler/CrawlJobHealthIndicator';
import { EdgeFunctionLogs } from '@/components/admin/crawler/EdgeFunctionLogs';
import { ScheduledJobsManagement } from '@/components/admin/crawler/ScheduledJobsManagement';
import { toast } from "sonner";
import { useIsMobile } from '@/hooks/use-mobile';

const TradingViewCrawlerPage = () => {
  const [symbols, setSymbols] = useState('EURUSD, GBPUSD, USDJPY');
  const [pages, setPages] = useState(5);
  const [isStarting, setIsStarting] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [crawlHistory, setCrawlHistory] = useState<CrawlJob[]>([]);
  const [javaServiceUrl, setJavaServiceUrl] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isApplyingRls, setIsApplyingRls] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [recentActivity, setRecentActivity] = useState(false);
  const isMobile = useIsMobile();

  // Calculate smart refresh parameters
  const healthOptions = useMemo(() => {
    const runningJobsCount = crawlHistory.filter(job => job.status === 'running').length;
    return {
      runningJobsCount,
      hasRecentActivity: recentActivity
    };
  }, [crawlHistory, recentActivity]);

  // Use real-time hooks with smart refresh
  const { job: currentJob, isLoading: isCurrentJobLoading } = useCrawlJobRealtime(currentJobId);
  const { healthData, isLoading: isHealthLoading, refreshHealth } = useCrawlJobsHealth(healthOptions);

  // Track recent activity for faster refresh
  useEffect(() => {
    if (isStarting || currentJob?.status === 'running') {
      setRecentActivity(true);
      
      // Clear recent activity flag after 30 seconds
      const timeout = setTimeout(() => {
        setRecentActivity(false);
      }, 30000);
      
      return () => clearTimeout(timeout);
    }
  }, [isStarting, currentJob?.status]);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Enhanced real-time subscription for jobs list
  useEffect(() => {
    let subscription: any = null;
    
    const setupSubscription = () => {
      subscription = TradingViewCrawlerService.subscribeToJobsList((jobs) => {
        console.log('📡 Real-time jobs list update received:', jobs.length);
        setCrawlHistory(jobs);
        
        // Track activity for smart refresh
        setRecentActivity(true);
        
        // Auto-set current job if there's a running one
        const runningJob = jobs.find(job => job.status === 'running');
        if (runningJob && runningJob.id !== currentJobId) {
          console.log('🔄 Setting current job to running job:', runningJob.id);
          setCurrentJobId(runningJob.id);
        } else if (!runningJob && currentJobId) {
          // Clear current job after a delay if no jobs are running
          setTimeout(() => {
            console.log('⏹️ Clearing current job - no running jobs found');
            setCurrentJobId(null);
          }, 3000);
        }
      });
    };

    // Initial setup
    setupSubscription();

    return () => {
      if (subscription) {
        console.log('🔌 Unsubscribing from jobs list updates');
        subscription.unsubscribe();
      }
    };
  }, [currentJobId]);

  const loadInitialData = async () => {
    setIsLoadingHistory(true);
    try {
      const [history, serviceUrl] = await Promise.all([
        TradingViewCrawlerService.getCrawlJobs(20),
        AppSettingsService.getJavaServiceUrl()
      ]);
      
      setCrawlHistory(history);
      setJavaServiceUrl(serviceUrl);
      
      // Set current job if there's a running one
      const runningJob = history.find(job => job.status === 'running');
      if (runningJob) {
        setCurrentJobId(runningJob.id);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load crawler data');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleStartCrawl = async () => {
    if (!symbols.trim()) {
      toast.error('Please enter at least one symbol');
      return;
    }

    if (pages < 1 || pages > 10) {
      toast.error('Pages must be between 1 and 10');
      return;
    }

    setIsStarting(true);
    setRecentActivity(true); // Enable fast refresh during start process
    
    try {
      const symbolsList = symbols.split(',').map(s => s.trim()).filter(s => s);
      
      // Create crawl job
      const createResult = await TradingViewCrawlerService.createCrawlJob({
        symbols: symbolsList,
        pages
      });

      if (!createResult.success || !createResult.jobId) {
        throw new Error(createResult.error || 'Failed to create crawl job');
      }

      setCurrentJobId(createResult.jobId);
      toast.success('Crawl job created successfully');

      // Start the crawl job (fire-and-forget)
      const startResult = await TradingViewCrawlerService.startCrawlJob(createResult.jobId);
      
      if (!startResult.success) {
        throw new Error(startResult.error || 'Failed to start crawl job');
      }

      toast.success('Crawl job is now running in the background');
      
    } catch (error) {
      console.error('Error during crawl:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start crawl');
      setCurrentJobId(null);
      setRecentActivity(false);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopCrawl = async () => {
    if (!currentJobId) return;

    setRecentActivity(true); // Enable fast refresh during stop process

    try {
      const result = await TradingViewCrawlerService.cancelCrawlJob(currentJobId);
      
      if (result.success) {
        toast.success('Crawl job cancelled');
        setCurrentJobId(null);
      } else {
        toast.error(result.error || 'Failed to cancel crawl job');
      }
    } catch (error) {
      console.error('Error cancelling crawl:', error);
      toast.error('Failed to cancel crawl job');
    }
  };

  const handleApplyRlsPolicies = async () => {
    setIsApplyingRls(true);
    try {
      // For now, just show a message that RLS policies should be applied manually
      toast.success('Please run the RLS policies migration file in your Supabase SQL editor');
    } catch (error) {
      console.error('Error applying RLS policies:', error);
      toast.error('Failed to apply RLS policies: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsApplyingRls(false);
    }
  };

  const toggleRowExpansion = (jobId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedRows(newExpanded);
  };

  const getJobHealth = (jobId: string) => {
    return healthData.find(h => h.id === jobId);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'running':
        return (
          <Badge variant="default" className="bg-blue-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Running
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="default" className="bg-green-500 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Failed
          </Badge>
        );
      case 'cancelled':
        return <Badge variant="secondary">Cancelled</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    if (!completedAt) return 'Running...';
    
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const diffMs = end.getTime() - start.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const minutes = Math.floor(diffSec / 60);
    const seconds = diffSec % 60;
    
    return `${minutes}m ${seconds}s`;
  };

  const getProgressPercentage = (job: CrawlJob) => {
    if (job.status === 'completed') return 100;
    if (job.status === 'failed' || job.status === 'cancelled') return 0;
    if (job.status === 'running') return 50;
    return 0;
  };

  const isJobRunning = currentJob?.status === 'running' || isStarting;

  if (isLoadingHistory) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
        <span className="ml-2">Loading crawler data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-6 w-6 text-emerald-500" />
          <h1 className="text-3xl font-bold">TradingView Ideas Crawler</h1>
        </div>
        <p className="text-muted-foreground">
          Crawl and analyze market ideas from TradingView for various trading pairs
        </p>
      </div>

      {/* Real-time Status Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Crawler Status</span>
            <div className="flex items-center gap-2">
              {isJobRunning ? (
                <Badge variant="default" className="bg-blue-500">
                  <Clock className="h-3 w-3 mr-1" />
                  Running
                </Badge>
              ) : (
                <Badge variant="secondary">Idle</Badge>
              )}
              <Button
                onClick={refreshHealth}
                variant="ghost"
                size="sm"
                disabled={isHealthLoading}
              >
                <Activity className="h-4 w-4" />
              </Button>
              {recentActivity && (
                <Badge variant="outline" className="text-xs animate-pulse">
                  Fast Refresh
                </Badge>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress indicator for current job */}
            {currentJob && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Job Progress</span>
                  <div className="flex items-center gap-2">
                    <span>{currentJob.status}</span>
                    {currentJob.status === 'running' && getJobHealth(currentJob.id) && (
                      <CrawlJobHealthIndicator
                        healthStatus={getJobHealth(currentJob.id)!.health_status}
                        secondsSinceHeartbeat={getJobHealth(currentJob.id)!.seconds_since_heartbeat}
                        lastHeartbeat={getJobHealth(currentJob.id)!.last_heartbeat}
                        retryCount={getJobHealth(currentJob.id)!.retry_count}
                        maxRetries={getJobHealth(currentJob.id)!.max_retries}
                      />
                    )}
                  </div>
                </div>
                <Progress 
                  value={getProgressPercentage(currentJob)} 
                  className="w-full"
                />
                {currentJob.status === 'running' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>
                      Processing {currentJob.symbols.join(', ')} - {currentJob.pages} pages each
                    </span>
                  </div>
                )}
                {currentJob.status === 'completed' && (
                  <div className="text-sm text-green-600">
                    ✅ Completed: {currentJob.success_count} ideas saved, {currentJob.skip_count} skipped
                  </div>
                )}
                {currentJob.status === 'failed' && (
                  <div className="text-sm text-red-600">
                    ❌ Failed: {currentJob.error_message}
                  </div>
                )}
              </div>
            )}
            
            {/* Status message */}
            <div className="flex items-center gap-4">
              {isJobRunning && !currentJob && (
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
                  <span className="text-sm text-muted-foreground">Starting crawl job...</span>
                </div>
              )}
              {!isJobRunning && crawlHistory.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  Last crawl: {crawlHistory[0].status === 'completed' ? 'completed successfully' : crawlHistory[0].status}
                </span>
              )}
              {!isJobRunning && crawlHistory.length === 0 && (
                <span className="text-sm text-muted-foreground">Ready to start crawling</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle>Crawler Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="symbols">Trading Symbols</Label>
              <Input
                id="symbols"
                value={symbols}
                onChange={(e) => setSymbols(e.target.value)}
                placeholder="EURUSD, GBPUSD, USDJPY"
                disabled={isJobRunning}
              />
              <p className="text-xs text-muted-foreground">
                Enter symbols separated by commas
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pages">Number of Pages</Label>
              <Input
                id="pages"
                type="number"
                value={pages}
                onChange={(e) => setPages(Number(e.target.value))}
                min={1}
                max={10}
                disabled={isJobRunning}
              />
              <p className="text-xs text-muted-foreground">
                Pages to crawl (1-10)
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="javaService">Java Service URL</Label>
            <Textarea
              id="javaService"
              value={javaServiceUrl}
              readOnly
              className="bg-muted"
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              Service endpoint (configured in app settings)
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleStartCrawl}
              disabled={isJobRunning}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Play className="h-4 w-4 mr-2" />
              Start Crawl
            </Button>
            
            <Button
              onClick={handleStopCrawl}
              disabled={!isJobRunning}
              variant="outline"
            >
              <Square className="h-4 w-4 mr-2" />
              Stop Crawl
            </Button>

            <Button
              onClick={loadInitialData}
              variant="outline"
              disabled={isJobRunning}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>

            <Button
              onClick={handleApplyRlsPolicies}
              disabled={isApplyingRls || isJobRunning}
              variant="secondary"
            >
              <Settings className="h-4 w-4 mr-2" />
              {isApplyingRls ? 'Applying...' : 'Apply RLS Policies'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scheduled Jobs Management */}
      <ScheduledJobsManagement />

      {/* Enhanced Results History with Health Monitoring */}
      <Card>
        <CardHeader>
          <CardTitle>Crawl History</CardTitle>
        </CardHeader>
        <CardContent>
          {crawlHistory.length > 0 ? (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Symbols</TableHead>
                    <TableHead>Pages</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Health</TableHead>
                    <TableHead>Ideas Found</TableHead>
                    <TableHead>Success</TableHead>
                    <TableHead>Started At</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {crawlHistory.map((job) => {
                    const health = getJobHealth(job.id);
                    const isExpanded = expandedRows.has(job.id);
                    
                    return (
                      <React.Fragment key={job.id}>
                        <TableRow className="cursor-pointer" onClick={() => toggleRowExpansion(job.id)}>
                          <TableCell>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {job.symbols.map((symbol) => (
                                <Badge key={symbol} variant="outline" className="text-xs">
                                  {symbol}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>{job.pages}</TableCell>
                          <TableCell>{getStatusBadge(job.status)}</TableCell>
                          <TableCell>
                            {health && job.status === 'running' ? (
                              <CrawlJobHealthIndicator
                                healthStatus={health.health_status}
                                secondsSinceHeartbeat={health.seconds_since_heartbeat}
                                lastHeartbeat={health.last_heartbeat}
                                retryCount={health.retry_count}
                                maxRetries={health.max_retries}
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>{job.total_ideas}</TableCell>
                          <TableCell>{job.success_count}</TableCell>
                          <TableCell className="text-sm">
                            {new Date(job.started_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDuration(job.started_at, job.completed_at)}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={9} className="p-0">
                              <Collapsible open={true}>
                                <CollapsibleContent>
                                  <div className="p-4 bg-muted/20 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {job.edge_function_logs && job.edge_function_logs.length > 0 && (
                                        <EdgeFunctionLogs logs={job.edge_function_logs} />
                                      )}
                                      {job.logs && job.logs.length > 0 && (
                                        <Card>
                                          <CardHeader>
                                            <CardTitle className="text-sm">Java Service Logs</CardTitle>
                                          </CardHeader>
                                          <CardContent>
                                            <ScrollArea className="h-32 w-full">
                                              <div className="space-y-1">
                                                {job.logs.map((log, index) => (
                                                  <div key={index} className="text-xs font-mono break-words">
                                                    {log}
                                                  </div>
                                                ))}
                                              </div>
                                            </ScrollArea>
                                          </CardContent>
                                        </Card>
                                      )}
                                    </div>
                                    {job.error_message && (
                                      <Card>
                                        <CardHeader>
                                          <CardTitle className="text-sm text-red-600">Error Details</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                          <p className="text-sm text-red-600">{job.error_message}</p>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              No crawl jobs found. Start your first crawl to see results here.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradingViewCrawlerPage;
